import { z } from 'zod';
import { ErrorCodeSchema } from '../errors';
import { ProvisioningOutcome, ProvisioningOutcomeSchema } from '../enums';
import { LearningSuiteErrorTypeSchema } from './learningsuite';

/**
 * Input of one provisioning attempt. The domain loads current data from D1 and passes it to the
 * provisioner, so adapters never touch the database (§132: queue messages carry IDs only).
 */
export const ProvisioningRequestSchema = z
  .object({
    registrationId: z.uuid(),
    /** `registration:<id>:provision` (stable across retries, §25). */
    idempotencyKey: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    /** batch.learningsuite_group_id_snapshot – never the current company value (§23). */
    learningsuiteGroupId: z.string(),
    /** From a previous partial attempt; lets the adapter skip lookup/creation. */
    knownLearningsuiteUserId: z.string().nullable(),
  })
  .strict();
export type ProvisioningRequest = z.infer<typeof ProvisioningRequestSchema>;

/**
 * Result of one provisioning attempt (§22–§26, §134).
 * - SUCCESS: user exists and group membership verified; `learningsuiteUserId` required.
 * - RETRYABLE_ERROR: timeout, 429, 5xx, network – retry with backoff (`retryAfterSeconds` from
 *   `Retry-After` if present). Partial progress (`learningsuiteUserId`, `userCreated`) is reported.
 * - PERMANENT_ERROR: invalid group (LEARNINGSUITE_GROUP_NOT_FOUND), rejected request
 *   (LEARNINGSUITE_REJECTED) – registration → PROVISIONING_FAILED.
 */
export const ProvisioningResultSchema = z
  .object({
    outcome: ProvisioningOutcomeSchema,
    learningsuiteUserId: z.string().min(1).optional(),
    /** True if this attempt created the LearningSuite user. */
    userCreated: z.boolean().optional(),
    /** True once group membership is confirmed. */
    groupAdded: z.boolean().optional(),
    errorCode: ErrorCodeSchema.optional(),
    /** Fine-grained failure type (R1 §6), shown to admins as „Fehlertyp“ (§26). */
    errorType: LearningSuiteErrorTypeSchema.optional(),
    /** Sanitised, no PII/secrets, max 500 chars. */
    errorMessage: z.string().max(500).optional(),
    retryAfterSeconds: z.number().int().positive().optional(),
  })
  .strict()
  .refine((r) => r.outcome !== ProvisioningOutcome.SUCCESS || r.learningsuiteUserId !== undefined, {
    message: 'SUCCESS requires learningsuiteUserId',
  })
  .refine((r) => r.outcome === ProvisioningOutcome.SUCCESS || r.errorCode !== undefined, {
    message: 'Errors require errorCode',
  });
export type ProvisioningResult = z.infer<typeof ProvisioningResultSchema>;
